import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, getErrorMessage } from '../../api/client';
import FormField from '../../components/ui/FormField';
import { validate, required, minLength, maxLength } from '../../utils/validation';
import PasswordInput from '../../components/PasswordInput';
import './DemoPages.css';

const DemoSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');

  const [formData, setFormData] = useState({
    orgName: '',
    userName: '',
    password: '',
    confirmPassword: '',
  });

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdSlug, setCreatedSlug] = useState('');

  useEffect(() => {
    // Get token and email from session storage
    const storedToken = sessionStorage.getItem('demoToken');
    const storedEmail = sessionStorage.getItem('demoEmail');

    if (!storedToken || !storedEmail) {
      // No verified token, redirect to demo request
      navigate('/demo');
      return;
    }

    setToken(storedToken);
    setEmail(storedEmail);
  }, [navigate]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const orgNameError = validate(formData.orgName, [
      required(t('demo.setup.validation.orgNameRequired')),
      minLength(2, t('demo.setup.validation.orgNameTooShort')),
      maxLength(100, t('demo.setup.validation.orgNameTooLong')),
    ]);
    if (orgNameError) errors.orgName = orgNameError;

    if (formData.userName) {
      const userNameError = validate(formData.userName, [
        maxLength(100, t('demo.setup.validation.userNameTooLong')),
      ]);
      if (userNameError) errors.userName = userNameError;
    }

    const passwordError = validate(formData.password, [
      required(t('demo.setup.validation.passwordRequired')),
      minLength(8, t('demo.setup.validation.passwordTooShort')),
    ]);
    if (passwordError) errors.password = passwordError;

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('demo.setup.validation.passwordMismatch');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (fieldErrors[name]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const response = await api.public.createDemo({
        token,
        orgName: formData.orgName,
        password: formData.password,
        userName: formData.userName || undefined,
      });

      // Clear session storage
      sessionStorage.removeItem('demoToken');
      sessionStorage.removeItem('demoEmail');

      setCreatedSlug(response.data.slug);
    } catch (error) {
      setSubmitError(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getLoginUrl = () => {
    const currentHost = window.location.hostname;
    let loginUrl: string;

    if (currentHost.includes('.localhost') || currentHost === 'localhost' || currentHost === '127.0.0.1') {
      loginUrl = `http://${createdSlug}.localhost:${window.location.port || 3000}/login`;
    } else if (currentHost.endsWith('.eventik.up.railway.app') || currentHost === 'eventik.up.railway.app') {
      loginUrl = `https://${createdSlug}.eventik.up.railway.app/login`;
    } else if (currentHost.endsWith('.eventik.cz') || currentHost === 'eventik.cz') {
      loginUrl = `https://${createdSlug}.eventik.cz/login`;
    } else {
      loginUrl = `${window.location.protocol}//${createdSlug}.${currentHost}/login`;
    }

    return loginUrl;
  };

  if (createdSlug) {
    return (
      <div className="demo-page">
        <div className="demo-container">
          <div className="demo-success demo-created">
            <i className="fas fa-party-horn" style={{ fontSize: '4rem', color: 'var(--success, #28a745)' }}></i>
            <h1>{t('demo.setup.successTitle')}</h1>
            <p>{t('demo.setup.successMessage')}</p>

            <div className="demo-created-info">
              <div className="demo-info-item">
                <span className="demo-info-label">{t('demo.setup.yourOrg')}:</span>
                <span className="demo-info-value">{formData.orgName}</span>
              </div>
              <div className="demo-info-item">
                <span className="demo-info-label">{t('demo.setup.yourEmail')}:</span>
                <span className="demo-info-value">{email}</span>
              </div>
              <div className="demo-info-item demo-info-warning">
                <i className="fas fa-clock"></i>
                <span>{t('demo.setup.demoExpires')}</span>
              </div>
            </div>

            <a href={getLoginUrl()} className="btn btn--primary btn--lg">
              {t('demo.setup.loginNow')} <i className="fas fa-sign-in-alt"></i>
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <div className="demo-container">
        <Link to="/" className="demo-back-link">
          <i className="fas fa-arrow-left"></i> {t('demo.backToHome')}
        </Link>

        <div className="demo-card demo-setup-card">
          <div className="demo-card-header">
            <i className="fas fa-cog"></i>
            <h1>{t('demo.setup.title')}</h1>
            <p>{t('demo.setup.subtitle')}</p>
          </div>

          <div className="demo-email-badge">
            <i className="fas fa-envelope"></i>
            {email}
          </div>

          <form onSubmit={handleSubmit} className="demo-form">
            {submitError && (
              <div className="alert alert-danger" role="alert">
                {submitError}
              </div>
            )}

            <FormField
              label={t('demo.setup.orgName')}
              htmlFor="demo-org-name"
              required
              error={fieldErrors.orgName}
              helperText={t('demo.setup.orgNameHelp')}
            >
              <input
                type="text"
                id="demo-org-name"
                name="orgName"
                className="form-control"
                value={formData.orgName}
                onChange={handleChange}
                placeholder={t('demo.setup.orgNamePlaceholder')}
                disabled={isSubmitting}
                autoFocus
              />
            </FormField>

            <FormField
              label={t('demo.setup.userName')}
              htmlFor="demo-user-name"
              error={fieldErrors.userName}
              helperText={t('demo.setup.userNameHelp')}
            >
              <input
                type="text"
                id="demo-user-name"
                name="userName"
                className="form-control"
                value={formData.userName}
                onChange={handleChange}
                placeholder={t('demo.setup.userNamePlaceholder')}
                disabled={isSubmitting}
              />
            </FormField>

            <FormField
              label={t('demo.setup.password')}
              htmlFor="demo-password"
              required
              error={fieldErrors.password}
            >
              <PasswordInput
                id="demo-password"
                name="password"
                className="form-control"
                value={formData.password}
                onChange={handleChange}
                placeholder={t('demo.setup.passwordPlaceholder')}
                disabled={isSubmitting}
              />
            </FormField>

            <FormField
              label={t('demo.setup.confirmPassword')}
              htmlFor="demo-confirm-password"
              required
              error={fieldErrors.confirmPassword}
            >
              <PasswordInput
                id="demo-confirm-password"
                name="confirmPassword"
                className="form-control"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder={t('demo.setup.confirmPasswordPlaceholder')}
                disabled={isSubmitting}
              />
            </FormField>

            <button
              type="submit"
              className="btn btn--primary btn--lg btn--block"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> {t('demo.setup.creating')}
                </>
              ) : (
                <>
                  <i className="fas fa-rocket"></i> {t('demo.setup.submit')}
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default DemoSetupPage;
